import React, { useState, useEffect } from "react";
import { Spinner } from "reactstrap";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const MonthlyDetectionChart = ({ year, month }) => {
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!year || !month) return;

    setLoading(true);
    setError(false);

    fetch(`${API_BASE}/detections/monthly?year=${year}&month=${month}`)
      .then((res) => res.json())
      .then((data) => {
        setMonthlyData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching monthly data:", err);
        setError(true);
        setLoading(false);
      });
  }, [year, month]);

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner color="primary" size="sm" />
      </div>
    );
  }

  if (error || !monthlyData) {
    return <p style={{ color: "gray", fontSize: "0.9rem" }}>No data available</p>;
  }

  // Create a map of dates to counts
  const dateMap = {};
  monthlyData.dailyCounts.forEach((day) => {
    const dayNum = parseInt(day.date.split("-")[2], 10);
    dateMap[dayNum] = day.count;
  });

  // Get the number of days in the month
  const daysInMonth = new Date(year, month, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Find max count for scaling
  const maxCount = Math.max(...days.map((d) => dateMap[d] || 0), 1);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <div style={{ width: "100%" }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: "12px",
        flexWrap: "wrap",
        gap: "8px"
      }}>
        <h5 style={{ margin: 0, fontWeight: "600", color: "#32325d" }}>
          {monthNames[month - 1]} {year}
        </h5>
        <div style={{
          display: "flex",
          gap: "20px",
          fontSize: "0.875rem",
          color: "#8898aa"
        }}>
          <span><strong style={{ color: "#32325d" }}>{monthlyData.totalDetections}</strong> detections</span>
          <span><strong style={{ color: "#32325d" }}>{monthlyData.speciesCount}</strong> species</span>
        </div>
      </div>

      <div style={{
        display: "flex",
        alignItems: "flex-end",
        gap: "2px",
        height: "80px",
        padding: "8px 0",
        borderRadius: "4px"
      }}>
        {days.map((day) => {
          const count = dateMap[day] || 0;
          const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
          const hasData = count > 0;

          return (
            <div
              key={day}
              style={{
                flex: 1,
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end"
              }}
              title={`Day ${day}: ${count} detection${count !== 1 ? 's' : ''}`}
            >
              <div
                style={{
                  height: hasData ? `${height}%` : "2px",
                  backgroundColor: hasData ? "#5e72e4" : "#e9ecef",
                  borderRadius: "2px 2px 0 0",
                  transition: "all 0.2s ease",
                  cursor: hasData ? "pointer" : "default",
                  minHeight: hasData ? "3px" : "2px"
                }}
                onMouseEnter={(e) => {
                  if (hasData) {
                    e.target.style.backgroundColor = "#324cdd";
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasData) {
                    e.target.style.backgroundColor = "#5e72e4";
                  }
                }}
              />
            </div>
          );
        })}
      </div>

      <div style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: "0.75rem",
        color: "#8898aa",
        marginTop: "4px",
        paddingTop: "4px"
      }}>
        <span>1</span>
        <span>{Math.floor(daysInMonth / 2)}</span>
        <span>{daysInMonth}</span>
      </div>
    </div>
  );
};

export default MonthlyDetectionChart;
